import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { XIcon } from "@phosphor-icons/react";

import api, { formatApiError } from "../../lib/api";
import {
  ErrorBanner,
  Field,
  INPUT_CLS,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
  TextInput,
  DateInput,
} from "../crm/Form";
import {
  createRMA,
  RMA_COMPONENT_LABEL,
  RMA_COMPONENT_TYPES,
  RMA_VENDOR_LABEL,
  RMA_VENDORS,
  relativeTime,
  shortId,
} from "../../lib/rma";

/**
 * Phase RMA-4 — Create RMA modal.
 *
 * Searchable diagnostic-report combobox + 6 form fields.
 * On 422, parse Pydantic detail into per-field error messages.
 *
 * Phase RMA-5 — when `lockedReportId` is provided, the searchable report
 * selector is hidden entirely and replaced by a read-only chip showing
 * `lockedReportLabel`. The id is seeded into form state on every open.
 */
export default function CreateRMAModal({
  open,
  onClose,
  lockedReportId,
  lockedReportLabel,
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState(() => _emptyForm(lockedReportId));
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitErr, setSubmitErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset state every time the modal is opened.
  useEffect(() => {
    if (open) {
      setForm(_emptyForm(lockedReportId));
      setFieldErrors({});
      setSubmitErr("");
      setBusy(false);
    }
  }, [open, lockedReportId]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((e) => ({ ...e, [k]: undefined }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setSubmitErr("");
    setFieldErrors({});

    if (!form.diagnostic_report_id) {
      setFieldErrors({ diagnostic_report_id: "Required" });
      return;
    }

    setBusy(true);
    const body = {
      diagnostic_report_id: form.diagnostic_report_id,
      vendor: form.vendor,
      component_type: form.component_type,
    };
    if (form.component_serial.trim()) body.component_serial = form.component_serial.trim();
    if (form.component_model.trim()) body.component_model = form.component_model.trim();
    if (form.warranty_expiry) {
      // <input type=date> gives YYYY-MM-DD — promote to ISO 8601 UTC
      body.warranty_expiry = `${form.warranty_expiry}T00:00:00+00:00`;
    }
    if (form.internal_notes.trim()) body.internal_notes = form.internal_notes.trim();

    try {
      const created = await createRMA(body);
      toast.success(`RMA ${created.rma_number} created`);
      onClose?.();
      navigate(`/rma-records/${created.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const map = {};
        for (const d of detail) {
          const last = d?.loc?.[d.loc.length - 1];
          if (last) map[last] = d?.msg || "Invalid";
        }
        setFieldErrors(map);
      } else if (typeof detail === "string") {
        setSubmitErr(detail);
      } else {
        setSubmitErr(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New RMA"
      subtitle="Create a vendor warranty claim from a finalized diagnostic report"
      data-testid="create-rma-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <ErrorBanner message={submitErr} />

        {lockedReportId ? (
          <Field label="Linked report">
            <div
              data-testid="rma-create-locked-report"
              className="rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm font-data text-slate-900"
            >
              {lockedReportLabel || "Linked report"}
            </div>
          </Field>
        ) : (
          <Field label="Diagnostic report" required>
            <ReportSearchSelect
              value={form.diagnostic_report_selected}
              onChange={(picked) =>
                setForm((f) => ({
                  ...f,
                  diagnostic_report_id: picked?.id || null,
                  diagnostic_report_selected: picked,
                }))
              }
              error={fieldErrors.diagnostic_report_id}
            />
          </Field>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Vendor" required>
            <Select
              value={form.vendor}
              onChange={(v) => setField("vendor", v)}
              options={RMA_VENDORS.map((v) => ({
                value: v,
                label: RMA_VENDOR_LABEL[v],
              }))}
              data-testid="rma-create-vendor"
            />
            {fieldErrors.vendor && <FieldError msg={fieldErrors.vendor} />}
          </Field>
          <Field label="Component type" required>
            <Select
              value={form.component_type}
              onChange={(v) => setField("component_type", v)}
              options={RMA_COMPONENT_TYPES.map((c) => ({
                value: c,
                label: RMA_COMPONENT_LABEL[c],
              }))}
              data-testid="rma-create-component-type"
            />
            {fieldErrors.component_type && (
              <FieldError msg={fieldErrors.component_type} />
            )}
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Component serial">
            <TextInput
              value={form.component_serial}
              onChange={(v) => setField("component_serial", v)}
              maxLength={100}
              placeholder="SN-…"
              data-testid="rma-create-serial"
            />
            {fieldErrors.component_serial && (
              <FieldError msg={fieldErrors.component_serial} />
            )}
          </Field>
          <Field label="Component model">
            <TextInput
              value={form.component_model}
              onChange={(v) => setField("component_model", v)}
              maxLength={100}
              placeholder="IQ8M, etc."
              data-testid="rma-create-model"
            />
            {fieldErrors.component_model && (
              <FieldError msg={fieldErrors.component_model} />
            )}
          </Field>
        </div>

        <Field label="Warranty expiry">
          <DateInput
            value={form.warranty_expiry}
            onChange={(v) => setField("warranty_expiry", v)}
            data-testid="rma-create-warranty"
          />
        </Field>

        <Field label="Internal notes" hint={`${form.internal_notes.length}/2000`}>
          <TextArea
            value={form.internal_notes}
            onChange={(v) => setField("internal_notes", v)}
            rows={3}
            maxLength={2000}
            data-testid="rma-create-notes"
          />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="rma-create-submit"
          >
            {busy ? "Creating…" : "Create RMA"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}

function _emptyForm() {
  return {
    diagnostic_report_id: null,
    diagnostic_report_selected: null,
    vendor: "enphase",
    component_type: "microinverter",
    component_serial: "",
    component_model: "",
    warranty_expiry: "",
    internal_notes: "",
  };
}

function FieldError({ msg }) {
  return <p className="text-xs text-red-700 mt-1">{msg}</p>;
}

// ---------------------------------------------------------------------------
// Searchable diagnostic-report selector
// ---------------------------------------------------------------------------

function ReportSearchSelect({ value, onChange, error }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Debounced search.
  const search = useCallback(async (q) => {
    setLoading(true);
    try {
      const r = await api.get("/diagnostic-reports", {
        params: { status: "finalized", q, limit: 20 },
      });
      setResults(r.data?.items || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(() => search(query), 300);
    return () => clearTimeout(handle);
  }, [query, open, search]);

  if (value) {
    return (
      <div
        data-testid="rma-create-report-selected"
        className="flex items-center justify-between gap-3 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2.5 text-sm"
      >
        <div className="min-w-0">
          <div className="font-medium text-slate-900 truncate">
            WO-{shortId(value.work_order_id)} ·{" "}
            {value.customer_name || "(unknown customer)"}
          </div>
          <div className="text-[11px] text-slate-600">
            {value.finalized_at
              ? `Finalized ${relativeTime(value.finalized_at)}`
              : "Finalized —"}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="rounded-md p-1 text-slate-500 hover:bg-emerald-100"
          aria-label="Clear selection"
        >
          <XIcon size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Search by customer, address, or WO #…"
        data-testid="rma-create-report-search"
        className={INPUT_CLS}
      />
      {error && <FieldError msg={error} />}
      {open && (query.length > 0 || results.length > 0) && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-slate-300 bg-white shadow-lg">
          {loading && (
            <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500">
              No finalized reports match.
            </div>
          )}
          {!loading &&
            results.map((r) => (
              <button
                type="button"
                key={r.id}
                onClick={() => {
                  onChange(r);
                  setOpen(false);
                  setQuery("");
                }}
                className="block w-full px-3 py-2 text-left hover:bg-slate-50"
                data-testid={`rma-create-report-option-${r.id}`}
              >
                <div className="text-sm font-medium text-slate-900">
                  WO-{r.work_order_short || shortId(r.work_order_id)} ·{" "}
                  {r.customer_name || "(unknown customer)"}
                </div>
                <div className="text-[11px] text-slate-500">
                  {r.property_address || ""}
                  {r.finalized_at
                    ? ` · Finalized ${relativeTime(r.finalized_at)}`
                    : ""}
                </div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
